import React, { useState, useMemo, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import ChartBurgerMenu from "./ChartBurgerMenu";
import HeatmapChart from "./HeatmapChart";

// Robust date parser to handle inconsistent formats (space vs T)
const parseDateSafe = (dateStr) => {
  if (!dateStr) return NaN;
  if (typeof dateStr !== "string") return new Date(dateStr).getTime();

  // Normalize format: replace space with T if 10th char is space and 11th is digit
  let normalized = dateStr;
  if (dateStr.length >= 11 && dateStr[10] === " " && /\d/.test(dateStr[11])) {
    normalized = dateStr.substring(0, 10) + "T" + dateStr.substring(11);
  }
  const ts = new Date(normalized).getTime();
  return ts;
};

const SolarGraph = ({
  data = [],
  dateRange,
  granularity = "hourly",
  onGranularityChange,
}) => {
  const chartRef = useRef(null);
  const [viewMode, setViewMode] = useState("line"); // 'line' | 'heatmap'

  // Generate unique IDs for this instance to avoid ApexCharts ID collisions
  const instanceId = useMemo(
    () => `solar-${Math.random().toString(36).substring(2, 9)}`,
    [],
  );
  const mainChartId = `${instanceId}-main`;
  const brushChartId = `${instanceId}-brush`;

  const processSeriesData = useCallback((rawData, key) => {
    if (!rawData || !Array.isArray(rawData)) return [];

    // 1. Map to [ts, value] and filter invalid/non-numeric
    const mapped = rawData
      .map((item) => {
        const ts = parseDateSafe(item.timestamp);
        let val = item[key];
        if (typeof val === "string" && val.trim() === "") val = 0;
        else val = Number(val);

        if (!Number.isFinite(ts) || !Number.isFinite(val)) return null;
        return [ts, val];
      })
      .filter((pt) => pt !== null);

    // 2. Sort by timestamp (ApexCharts requirement for datetime x-axis)
    mapped.sort((a, b) => a[0] - b[0]);

    // 3. Deduplicate (keep latest value for same timestamp)
    const deduplicated = [];
    for (const pt of mapped) {
      if (deduplicated.length > 0 && pt[0] === deduplicated[deduplicated.length - 1][0]) {
        deduplicated[deduplicated.length - 1] = pt;
      } else {
        deduplicated.push(pt);
      }
    }

    return deduplicated;
  }, []);

  const feedToGridData = useMemo(
    () => processSeriesData(data, "Grid_Export_kWh"),
    [data, processSeriesData],
  );
  const receiveFromGridData = useMemo(
    () => processSeriesData(data, "Grid_Import_kWh"),
    [data, processSeriesData],
  );

  const series = useMemo(() => [
    {
      name: "Feed to Grid (Export)",
      data: feedToGridData,
    },
    {
      name: "Receive from Grid (Import)",
      data: receiveFromGridData,
    },
  ], [feedToGridData, receiveFromGridData]);

  // Main Chart Options - Memoized for stability
  const chartOptions = useMemo(() => ({
    chart: {
      id: mainChartId,
      type: "area",
      height: 350,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ["#22c55e", "#ef4444"], // Green (Export), Red (Import)
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.6,
        opacityTo: 0.1,
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
      markers: {
        radius: 12,
      },
    },
    xaxis: {
      type: "datetime",
      tooltip: {
        enabled: true,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      title: {
        text: "Solar Usage",
        style: {
          color: "#64748b",
          fontWeight: 500,
        },
      },
      labels: {
        formatter: (val) => (typeof val === "number" ? val.toFixed(2) : val),
      },
      min: 0,
    },
    tooltip: {
      y: {
        formatter: (val) => (typeof val === "number" ? `${val.toFixed(2)} kWh` : "–"),
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 0,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }), [mainChartId]);

  // Calculate the actual range of the data to safeguard the brush selection
  const dataRangeTimestamps = useMemo(() => {
    const allTs = [...feedToGridData, ...receiveFromGridData].map((pt) => pt[0]);
    if (allTs.length === 0) return null;
    return { min: Math.min(...allTs), max: Math.max(...allTs) };
  }, [feedToGridData, receiveFromGridData]);

  // Brush Chart Options - Memoized for stability
  const brushOptions = useMemo(() => ({
    chart: {
      id: brushChartId,
      height: 130,
      type: "area",
      brush: {
        target: mainChartId,
        enabled: true,
      },
      selection: {
        enabled: true,
        xaxis: {
          min: (() => {
            if (!dateRange?.start || !dataRangeTimestamps) return undefined;
            const s = parseDateSafe(dateRange.start);
            const e = parseDateSafe(dateRange.end || Date.now());
            if (Number.isNaN(s) || Number.isNaN(e)) return undefined;

            const minClamp = Math.max(s, dataRangeTimestamps.min);
            const maxClamp = Math.min(e, dataRangeTimestamps.max);

            return minClamp < maxClamp ? minClamp : undefined;
          })(),
          max: (() => {
            if (!dateRange?.end || !dataRangeTimestamps) return undefined;
            const s = parseDateSafe(dateRange.start || 0);
            const e = parseDateSafe(dateRange.end);
            if (Number.isNaN(s) || Number.isNaN(e)) return undefined;

            const minClamp = Math.max(s, dataRangeTimestamps.min);
            const maxClamp = Math.min(e, dataRangeTimestamps.max);

            return minClamp < maxClamp ? maxClamp : undefined;
          })(),
        },
      },
      toolbar: {
        show: false,
      },
    },
    colors: ["#22c55e", "#ef4444"], // Same colors
    fill: {
      type: "solid",
      opacity: 0.6,
    },
    stroke: {
      width: 1,
    },
    xaxis: {
      type: "datetime",
      tooltip: {
        enabled: false,
      },
    },
    legend: {
      show: false,
    },
    yaxis: {
      show: false,
      tickAmount: 2,
    },
  }), [brushChartId, mainChartId, dateRange, dataRangeTimestamps]);

  const hasData = useMemo(() => {
    return feedToGridData.length > 0 || receiveFromGridData.length > 0;
  }, [feedToGridData, receiveFromGridData]);

  return (
    <div
      style={{
        marginTop: 24,
        padding: "20px",
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* Header Tabs & Burger Menu */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            style={{
              padding: "8px 16px",
              background: "#0284c7",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              fontWeight: 600,
              cursor: "default",
              fontSize: "14px",
            }}
          >
            {granularity === "minute"
              ? "Minute-wise Solar Import / Export"
              : "Hourly Solar Import / Export"}
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "inline-flex",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setViewMode("line");
                onGranularityChange?.("hourly");
              }}
              style={{
                padding: "6px 12px",
                background: viewMode === "line" && granularity === "hourly" ? "#0ea5e9" : "#ffffff",
                color: viewMode === "line" && granularity === "hourly" ? "#ffffff" : "#334155",
                border: "none",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Hour-wise
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode("line");
                onGranularityChange?.("minute");
              }}
              style={{
                padding: "6px 12px",
                background: viewMode === "line" && granularity === "minute" ? "#0ea5e9" : "#ffffff",
                color: viewMode === "line" && granularity === "minute" ? "#ffffff" : "#334155",
                border: "none",
                borderLeft: "1px solid #cbd5e1",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Minute-wise
            </button>
            <button
              type="button"
              onClick={() => setViewMode("heatmap")}
              style={{
                padding: "6px 12px",
                background: viewMode === "heatmap" ? "#0ea5e9" : "#ffffff",
                color: viewMode === "heatmap" ? "#ffffff" : "#334155",
                border: "none",
                borderLeft: "1px solid #cbd5e1",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Heat Map
            </button>
          </div>

          {/* Burger Menu */}
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle={
              granularity === "minute"
                ? "Solar_Analytics_Minute_Wise"
                : "Solar_Analytics_Hourly"
            }
            exportContainer={true}
            chartData={{
              labels: data.map((d) => {
                const ts = parseDateSafe(d.timestamp);
                return Number.isNaN(ts) ? "–" : new Date(ts).toLocaleString();
              }),
              series: [
                feedToGridData.map((d) => d[1]),
                receiveFromGridData.map((d) => d[1]),
              ],
              seriesNames: [
                "Feed to Grid (Export)",
                "Receive from Grid (Import)",
              ],
            }}
            chartType="solar"
          />
        </div>
      </div>

      {/* Main Chart */}
      <div
        id="chart-wrapper"
        ref={chartRef}
        style={{ background: "#fff", padding: "10px", minHeight: hasData ? "auto" : "200px" }}
      >
        {!hasData ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px", color: "#64748b" }}>
            No valid data points for this selection
          </div>
        ) : viewMode === "heatmap" ? (
          <SolarHeatmapSection data={data} />
        ) : (
          <>
            <ReactApexChart
              key={`${mainChartId}-${granularity}`}
              options={chartOptions}
              series={series}
              type="area"
              height={300}
            />
            {/* Brush Chart - only show if we have enough points for meaningful selection */}
            {feedToGridData.length + receiveFromGridData.length > 2 && (
              <div style={{ marginTop: "-20px" }}>
                <ReactApexChart
                  key={brushChartId}
                  options={brushOptions}
                  series={series.slice(0, 1)} // Simplify brush chart to first series only
                  type="area"
                  height={100}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SolarGraph;

/* ==========================================Added
   SOLAR HEATMAP SECTION
   ========================================== */
function SolarHeatmapSection({ data }) {
  // Define structured Heatmap Metadata
  const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const HOUR_LABELS = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
  }, []);

  // Structural transformation: Maps linear timestamp matrix arrays into a 7x24 weekly matrix
  const processedHeatmap = useMemo(() => {
    if (!data || data.length === 0) {
      return { rows: [], minValue: null, maxValue: null };
    }

    // Accumulators to average out overlapping days of the week (e.g. if looking at a 30-day range)
    const gridSums = Array.from({ length: 7 }, () => Array(24).fill(0));
    const gridCounts = Array.from({ length: 7 }, () => Array(24).fill(0));

    data.forEach((item) => {
      const val = item.Solar_Generated_kWh;
      if (val === null || val === undefined || Number.isNaN(val)) return;

      const timeMs = parseDateSafe(item.timestamp);
      if (Number.isNaN(timeMs)) return;

      const date = new Date(timeMs);

      // Map standard getDay() [0=Sun, 1=Mon...6=Sat] into standard matrix rows [0=Mon...6=Sun]
      const currentDay = date.getDay();
      const dayIdx = currentDay === 0 ? 6 : currentDay - 1;
      const hourIdx = date.getHours();

      gridSums[dayIdx][hourIdx] += val;
      gridCounts[dayIdx][hourIdx] += 1;
    });

    let globalMin = Infinity;
    let globalMax = -Infinity;

    const structuredRows = DAYS_OF_WEEK.map((dayLabel, dIdx) => {
      const values = Array.from({ length: 24 }, (_, hIdx) => {
        const count = gridCounts[dIdx][hIdx];
        if (count === 0) return null; // keeps unrecorded hours empty instead of 0

        const averageValue = gridSums[dIdx][hIdx] / count;

        if (averageValue < globalMin) globalMin = averageValue;
        if (averageValue > globalMax) globalMax = averageValue;

        return Number(averageValue.toFixed(2));
      });

      return {
        label: dayLabel,
        values: values,
      };
    });

    return {
      rows: structuredRows,
      minValue: globalMin === Infinity ? null : globalMin,
      maxValue: globalMax === -Infinity ? null : globalMax,
    };
  }, [data, DAYS_OF_WEEK]);

  if (!processedHeatmap.rows.length) return <div style={{ padding: 20, textAlign: "center" }}>No solar data available for heatmap</div>;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: 10,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      }}
    >
      <h3 style={{ marginBottom: 12, color: "#0f172a" }}>Solar Heatmap</h3>
      <HeatmapChart
        rows={processedHeatmap.rows}
        timeLabels={HOUR_LABELS}
        minValue={processedHeatmap.minValue}
        maxValue={processedHeatmap.maxValue}
        loading={false}
        metricLabel="Solar kW"
        machineColor="#059669"
      />
    </div>
  );
}
